import type { CipherDirection } from "@/lib/tool-kit/ciphers-polygraphic/keyed-letters";
import {
  buildSquare,
  type GridAlphabet,
  gridText,
  type SquareFill,
  squarePoint,
} from "@/lib/tool-kit/ciphers-polygraphic/squares";

/**
 * The four-square cipher (Félix Delastelle).
 *
 * Four 5 × 5 squares are laid out in a 2 × 2 block. The upper-left and lower-right
 * squares are the plain alphabet; the upper-right and lower-left are keyed, each with its
 * own keyword. To encipher a pair of letters, find the first in the upper-left square and
 * the second in the lower-right one. They are opposite corners of a rectangle: the cipher
 * pair is at the other two corners — the first cipher letter in the upper-right square (in
 * the first plain letter's row and the second's column), the second in the lower-left
 * square (in the second plain letter's row and the first's column).
 *
 * Unlike Playfair, a doubled pair needs no filler and a pair never comes out reversed;
 * only an odd last letter is padded.
 */

export interface FourSquareOptions {
  /** Keyword of the upper-right square. */
  keyword1: string;
  /** Keyword of the lower-left square. */
  keyword2: string;
  kind: Exclude<GridAlphabet, "alnum">;
  fill: SquareFill;
}

export interface FourSquareSquares {
  upperLeft: string;
  upperRight: string;
  lowerLeft: string;
  lowerRight: string;
}

export const FOUR_SQUARE_SIZE = 5;

export function fourSquareSquares(
  options: FourSquareOptions
): FourSquareSquares {
  const plain = buildSquare("", options.kind, "rows");
  return {
    upperLeft: plain,
    upperRight: buildSquare(options.keyword1, options.kind, options.fill),
    lowerLeft: buildSquare(options.keyword2, options.kind, options.fill),
    lowerRight: plain,
  };
}

/** One pair, with the cells that were used so a widget can mark them. */
export interface FourSquareStep {
  plain: string;
  cipher: string;
  /** Row/column (0-based) of each letter in the square it was found in. */
  plainFirst: { row: number; column: number };
  plainSecond: { row: number; column: number };
  /** The cipher letters' cells: first in the upper-right square, second in the lower-left. */
  cipherFirst: { row: number; column: number };
  cipherSecond: { row: number; column: number };
  /** True when the second plain letter is padding the preparation added. */
  filled: boolean;
}

export interface FourSquareResult {
  squares: FourSquareSquares;
  steps: FourSquareStep[];
  output: string;
  droppedQ: number;
  /** Decoding: a last letter with no partner. */
  unpaired: string;
}

function cellAt(square: string, row: number, column: number): string {
  return square.charAt(row * FOUR_SQUARE_SIZE + column);
}

function pairsOf(
  chars: string,
  padWith: string
): { pair: string; filled: boolean }[] {
  const pairs: { pair: string; filled: boolean }[] = [];
  for (let index = 0; index < chars.length; index += 2) {
    const first = chars.charAt(index);
    const second = chars.charAt(index + 1);
    if (second === "") {
      const pad = first === padWith ? "Z" : padWith;
      pairs.push({ pair: `${first}${pad}`, filled: true });
    } else {
      pairs.push({ pair: `${first}${second}`, filled: false });
    }
  }
  return pairs;
}

export function fourSquareTransform(
  text: string,
  options: FourSquareOptions,
  direction: CipherDirection
): FourSquareResult {
  const squares = fourSquareSquares(options);
  const { chars, droppedQ } = gridText(text, options.kind);
  const steps: FourSquareStep[] = [];
  const size = FOUR_SQUARE_SIZE;
  const odd = chars.length % 2 === 1;
  const unpaired = direction === "decode" && odd ? chars.slice(-1) : "";
  const usable = unpaired === "" ? chars : chars.slice(0, -1);
  const pairs = pairsOf(usable, "X");
  for (const { pair, filled } of pairs) {
    const a = pair.charAt(0);
    const b = pair.charAt(1);
    if (direction === "encode") {
      const first = squarePoint(squares.upperLeft, size, a);
      const second = squarePoint(squares.lowerRight, size, b);
      const cipherFirst = { row: first.row, column: second.column };
      const cipherSecond = { row: second.row, column: first.column };
      steps.push({
        plain: pair,
        cipher: `${cellAt(squares.upperRight, cipherFirst.row, cipherFirst.column)}${cellAt(squares.lowerLeft, cipherSecond.row, cipherSecond.column)}`,
        plainFirst: first,
        plainSecond: second,
        cipherFirst,
        cipherSecond,
        filled,
      });
    } else {
      const cipherFirst = squarePoint(squares.upperRight, size, a);
      const cipherSecond = squarePoint(squares.lowerLeft, size, b);
      const first = { row: cipherFirst.row, column: cipherSecond.column };
      const second = { row: cipherSecond.row, column: cipherFirst.column };
      steps.push({
        plain: `${cellAt(squares.upperLeft, first.row, first.column)}${cellAt(squares.lowerRight, second.row, second.column)}`,
        cipher: pair,
        plainFirst: first,
        plainSecond: second,
        cipherFirst,
        cipherSecond,
        filled: false,
      });
    }
  }
  const output = steps
    .map((step) => (direction === "encode" ? step.cipher : step.plain))
    .join("");
  return { squares, steps, output, droppedQ, unpaired };
}
